import { Component, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { SidenavMenuComponent } from './layouts/sidenav-menu/sidenav-menu.component';
import { AuthGuardV2Service } from './v2/services/auth-guard/AuthGuardV2Service';
import { CustomPreloadingStrategyService } from '@service/customPreloadingStrategy/custom-preloading-strategy.service';
import { environment } from '@environment/environment';

const routes: Routes = [
	{ path: '', redirectTo: '/login', pathMatch: 'full' },
	{
		path: 'login',
		loadChildren: () => import('./v2/components/login/login.module').then((m) => m.LoginModule),
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'capacity',
		component: SidenavMenuComponent,
		loadChildren: () =>
			import('./capacity-search/capacity-search.module').then((m) => m.CapacitySearchModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'users',
		component: SidenavMenuComponent,
		loadChildren: () => import('./broker/broker.module').then((m) => m.BrokerModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['BROKER', 'SHIPPER', 'CARRIER', 'ADMIN'], role: ['ALL'] },
	},

	{
		path: 'projects',
		component: SidenavMenuComponent,
		loadChildren: () => import('./project/project.module').then((m) => m.ProjectModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'v2/projects',
		component: SidenavMenuComponent,
		loadChildren: () => import('./v2/project/project.module').then((m) => m.ProjectModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'carriers',
		component: SidenavMenuComponent,
		loadChildren: () => import('./carriers/carriers.module').then((m) => m.CarriersModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'v2/carriers',
		component: SidenavMenuComponent,
		loadChildren: () =>
			import('./v2/carriers-network/carriers-network.module').then((m) => m.CarriersNetworkModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'v2/loads',
		component: SidenavMenuComponent,
		loadChildren: () => import('./v2/components/loads/loads.module').then((m) => m.LoadsModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'v2/loads/:quoteId',
		component: SidenavMenuComponent,
		loadChildren: () =>
			import('./v2/components/quote-details/quote-details-v2.module').then(
				(m) => m.QuoteDetailsV2Module
			),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'admin',
		component: SidenavMenuComponent,
		loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ADMIN'], role: ['ALL'] },
	},
	{
		path: 'bid',
		data: { preload: true, loadAfterSeconds: 2 },
		loadChildren: () => import('./bid/bid.module').then((m) => m.BidModule),
	},
	{
		path: 'loadsSourcing',
		data: { preload: true, loadAfterSeconds: 2 },
		loadChildren: () =>
			import('./loads-matching/loads-matching.module').then((m) => m.LoadsMatchingModule),
	},
	// enable this route if rate confirmation feature is enabled
	// {
	// 	path: 'signRateConfirmation/:bookingId',
	// 	loadChildren: () =>
	// 		import('./sign-rate-confirmation/sign-rate-confirmation.module').then(
	// 			(m) => m.SignRateConfirmationModule
	// 		),
	// },
	{
		path: 'register',
		loadChildren: () => import('./register/register.module').then((m) => m.RegisterModule),
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'register/carrier/success',
		loadChildren: () =>
			import('./carrier-registration-successful/carrier-registration-successful.module').then(
				(m) => m.CarrierRegistrationSuccessfulModule
			),
	},
	{
		path: 'user_survey',
		loadChildren: () => import('./user-survey/user-survey.module').then((m) => m.UserSurveyModule),
		data: { type: ['BROKER', 'SHIPPER'], role: ['ALL'] },
		canActivate: [AuthGuardV2Service],
	},
	{
		path: 'reset_password/:hashKey',
		loadChildren: () =>
			import('./reset-password/reset-password.module').then((m) => m.ResetPasswordModule),
	},
	{
		path: 'expired_link',
		loadChildren: () =>
			import('./expired-link/expired-link.module').then((m) => m.ExpiredLinkModule),
	},
	{
		path: 'forgot_password',
		loadChildren: () =>
			import('./reset-password/reset-password.module').then((m) => m.ResetPasswordModule),
	},
	{
		path: 'maintenance',
		loadChildren: () => import('./maintenance/maintenance.module').then((m) => m.MaintenanceModule),
	},
	{
		path: 'email_verified/:userRandomString/:setPassword',
		loadChildren: () =>
			import('./email-verified/email-verified.module').then((m) => m.EmailVerifiedModule),
	},
	{
		path: 'change_browser',
		loadChildren: () => import('./edge-ie-msg/edge-ie-msg.module').then((m) => m.EdgeIeMsgModule),
	},
	{
		path: 'project_rate_request',
		loadChildren: () =>
			import('./project-rate-request/project-rate-request.module').then(
				(m) => m.ProjectRateRequestModule
			),
	},
	{
		path: 'carrier',
		data: { type: ['CARRIER'] },
		component: SidenavMenuComponent,
		loadChildren: () =>
			import('./carriers-view/carriers-view.module').then((m) => m.CarriersViewModule),
		canActivate: [AuthGuardV2Service],
	},
	{
		path: 'pricing',
		component: SidenavMenuComponent,
		loadChildren: () => import('./pricing/pricing.module').then((m) => m.PricingModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'dashboard',
		component: SidenavMenuComponent,
		loadChildren: () => import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		component: SidenavMenuComponent,
		path: 'background-jobs',
		loadChildren: () =>
			import('./v2/components/background-jobs/background-jobs.module').then(
				(m) => m.BackgroundJobsModule
			),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{
		path: 'booking-assistant-automation',
		component: SidenavMenuComponent,
		loadChildren: () =>
			import(
				'./broker/automation/booking-assistant-automation/booking-assistant-automation.module'
			).then((m) => m.BookingAssistantAutomationModule),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
	{ path: '**', redirectTo: 'capacity', pathMatch: 'full' },
	{
		component: SidenavMenuComponent,
		path: 'background-jobs',
		loadChildren: () =>
			import('./v2/components/background-jobs/background-jobs.module').then(
				(m) => m.BackgroundJobsModule
			),
		canActivate: [AuthGuardV2Service],
		data: { type: ['ALL'], role: ['ALL'] },
	},
];

@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			useHash: true,
			preloadingStrategy: CustomPreloadingStrategyService,
		}),
	],
	exports: [RouterModule],
})
export class AppRoutingModule {}
